import CustomFilterService from '@/services/CustomFilterService'
import { createPreviewRequestId } from '@/lib/preview-request-id'
import { assignIn, omit } from 'lodash-es'
import Vue from 'vue'

const defaultCustomFilter = () => ({
  collectionName: '',
  companyFilters: [],
  locationFilters: [],
  analyticEventFilters: [],
  dateFilters: [],
  surveyFilters: [],
  tagFilters: [],
  longFormSurveyAnswerFilters: []
})

const INCLUDE = 'include'
const EXCLUDE = 'exclude'
const DEFAULT_BEHAVIOR = INCLUDE
const COLLECTION_NAMES = {
  CUSTOMERS: 'customers'
}

// Only the existing boolean setIsFetchAudiencePreview(true) callers set this; it matches no response.
const LEGACY_PENDING_PREVIEW = 'legacy'

const selectPromoId = (promos) => {
  if (Array.isArray(promos) && promos.length) {
    return { promoId: promos[0] }
  }
  return {}
}

export default {
  namespaced: true,
  state: {
    customFilter: defaultCustomFilter(),
    count: 0,
    previewCustomerList: [],
    pendingPreviewRequestId: null,
    skip: 0,
    limit: 3,
    isLoadingMore: false,
    isEventLimitReached: false
  },
  getters: {
    selectCustomFilter: (state) => state.customFilter,
    selectCount: (state) => state.count || 0,
    selectIsLoading: (state) => state.pendingPreviewRequestId !== null,
    selectPendingPreviewRequestId: (state) => state.pendingPreviewRequestId,
    selectPreviewCustomerList: (state) => state.previewCustomerList,
    selectLimit: (state) => state.limit,
    selectSkip: (state) => state.skip,
    selectCollectionName: (state) => state.customFilter.collectionName,
    selectCompanyFilters: (state) => state.customFilter.companyFilters,
    selectLocationFilters: (state) => state.customFilter.locationFilters,
    selectAnalyticEventFilters: (state) => state.customFilter.analyticEventFilters,
    selectDateFilters: (state) => state.customFilter.dateFilters,
    selectSurveyFilters: (state) => state.customFilter.surveyFilters,
    selectTagFilters: (state) => state.customFilter.tagFilters,
    selectFirstFilterCompanies: (state) =>
      state.customFilter.companyFilters?.length
        ? state.customFilter.companyFilters[0].companies
        : null,
    selectFirstFilterLocations: (state) =>
      state.customFilter.locationFilters?.length
        ? state.customFilter.locationFilters[0].locations
        : null,
    selectAllFilterActions: (state) =>
      state.customFilter.analyticEventFilters?.map((a) => {
        return {
          did: a.behavior === INCLUDE ? true : false,
          eventType: a.types[0],
          ...(a.campaigns?.length ? { campaignId: a.campaigns[0] } : {}),
          ...selectPromoId(a.promos)
        }
      }),
    selectFirstFilterDateSettings: (state) =>
      state.customFilter.dateFilters?.length ? state.customFilter.dateFilters[0] : null,
    selectFirstFilterRatingSurveyFilter: (state) =>
      state.customFilter.surveyFilters?.length
        ? state.customFilter.surveyFilters.find((sf) => sf.ratings)
        : null,
    selectFirstFilterLFSAnswer: (state) =>
      state.customFilter.longFormSurveyAnswerFilters?.length
        ? state.customFilter.longFormSurveyAnswerFilters[0]
        : null,
    selectFirstFilterTags: (state) =>
      state.customFilter.tagFilters?.length ? state.customFilter.tagFilters[0].tags : null,
    selectIsLoadingMore: (state) => state.isLoadingMore,
    selectIsEventLimitReached: (state) => state.isEventLimitReached
  },
  mutations: {
    SET_CUSTOM_FILTER(state, customFilter) {
      state.customFilter = assignIn(defaultCustomFilter(), customFilter)
    },
    SET_COUNT(state, count) {
      state.count = count
    },
    SET_PREVIEW_CUSTOMER_LIST(state, customerList) {
      state.previewCustomerList = customerList
    },
    SET_PENDING_PREVIEW_REQUEST_ID(state, requestId) {
      state.pendingPreviewRequestId = requestId
    },
    SET_SKIP(state, skip) {
      state.skip = skip
    },
    RESET_CUSTOM_FILTER(state) {
      state.customFilter = defaultCustomFilter()
    },
    SET_IS_LOAD_MORE(state, loadMore) {
      state.isLoadingMore = loadMore
    },
    SET_EVENT_LIMIT_REACHED(state, reached) {
      state.isEventLimitReached = reached
    }
  },
  actions: {
    async resetCustomFilter({ commit }) {
      commit('RESET_CUSTOM_FILTER')
    },

    // activeCampaignNotReceived (if passed) will be the campaignId of an 'auto' campaign being edited
    async formatFilterPayload({ getters, rootGetters }, activeCampaignNotReceived) {
      const eventTypes = rootGetters['actionFilter/selectAnalyticEventTypesV2']
      // check if current campaignSetup is of type 'auto' and if it is being edited (has and _id)
      // if so then add the analyticEventFilter for 'did not receive <currentCampaign>'
      const organization = rootGetters['organization/selectOrganization']
      const activeCompany = rootGetters['company/selectActiveCompany']
      const campaignSetup = rootGetters['campaignSetups/selectCampaignSetup']
      const isSavingOrUpdating = rootGetters['campaignSetups/selectIsSavingOrUpdating']
      const isEditingAutoCampaign = campaignSetup.type === 'auto' && campaignSetup._id
      const filters = getters.selectCustomFilter
      return {
        collectionName: filters.collectionName,
        ...(organization && activeCompany._id === 'ALL_COMPANIES'
          ? { organizationId: organization._id }
          : {}),
        // always pass companyFilters
        companyFilters: filters.companyFilters.length
          ? [...filters.companyFilters]
          : [
              {
                companies: rootGetters['company/selectRelevantCompanies'].map((c) => c._id),
                behavior: DEFAULT_BEHAVIOR
              }
            ],
        ...(filters.locationFilters.length && filters.locationFilters[0].locations.length
          ? { locationFilters: filters.locationFilters }
          : {}),
        ...(filters.dateFilters.length
          ? {
              dateFilters: filters.dateFilters.map((dF) =>
                dF.unitQuantity ? { ...dF, unitQuantity: parseInt(dF.unitQuantity) } : dF
              )
            }
          : {}),
        ...(filters.analyticEventFilters.length
          ? {
              analyticEventFilters: [
                ...filters.analyticEventFilters,
                ...(isEditingAutoCampaign && !isSavingOrUpdating
                  ? [
                      {
                        types: [eventTypes.SENT_CAMPAIGN],
                        behavior: EXCLUDE,
                        campaigns: [campaignSetup._id]
                      }
                    ]
                  : [])
              ]
            }
          : isEditingAutoCampaign && !isSavingOrUpdating
          ? {
              analyticEventFilters: [
                {
                  types: ['sent-campaign'],
                  behavior: EXCLUDE,
                  campaigns: [campaignSetup._id]
                }
              ]
            }
          : {}),
        ...(filters.surveyFilters.length ? { surveyFilters: filters.surveyFilters } : {}),
        ...(filters.tagFilters.length ? { tagFilters: filters.tagFilters } : {}),
        ...(filters.longFormSurveyAnswerFilters.length
          ? { longFormSurveyAnswerFilters: filters.longFormSurveyAnswerFilters }
          : {})
      }
    },

    async fetchAudiencePreview({ getters, commit, dispatch }, { loadMore }) {
      // A second page requested while one is in flight would supersede it and skip its rows.
      if (loadMore && getters.selectPendingPreviewRequestId !== null) return
      // The response arrives on a channel shared by every request of this user, so only the
      // response carrying the latest request's id is applied.
      const requestId = createPreviewRequestId()
      commit('SET_PENDING_PREVIEW_REQUEST_ID', requestId)
      commit('SET_SKIP', loadMore ? getters.selectSkip + getters.selectLimit : 0)
      commit('SET_IS_LOAD_MORE', loadMore ? true : false)
      const formattedFilters = await dispatch('formatFilterPayload')
      if (getters.selectPendingPreviewRequestId !== requestId) return
      try {
        await CustomFilterService.fetchPreview({
          customFilter: formattedFilters,
          skip: getters.selectSkip,
          limit: getters.selectLimit,
          requestId
        })
      } catch (error) {
        if (getters.selectPendingPreviewRequestId !== requestId) return
        Vue.notify({
          type: 'error',
          text: 'Something went wrong. Please try again try.'
        })
        commit('SET_PENDING_PREVIEW_REQUEST_ID', null)
      }
    },

    async resetCustomFilter({ commit }) {
      commit('SET_CUSTOM_FILTER', defaultCustomFilter())
    },

    setIsFetchAudiencePreview({ commit, getters }, status) {
      if (status && getters.selectPendingPreviewRequestId !== null) return
      commit('SET_PENDING_PREVIEW_REQUEST_ID', status ? LEGACY_PENDING_PREVIEW : null)
    },

    setAudiencePreviewCount({ commit }, count) {
      commit('SET_COUNT', count)
    },

    setIsEventLimitReached({ commit }, reached) {
      commit('SET_EVENT_LIMIT_REACHED', reached)
    },

    setPreviewCustomerList({ commit }, customers) {
      commit('SET_PREVIEW_CUSTOMER_LIST', customers)
    },

    async setCustomFilterFromCampaignSetup({ commit }, customFilter) {
      let newCustomFilter = omit(customFilter, ['__v', '_id', 'createdAt', 'updatedAt'])
      commit('SET_CUSTOM_FILTER', newCustomFilter)
    },

    async setCustomFilter(
      { commit, getters, rootGetters },
      {
        filterComponent,
        companyFilter,
        locationFilter,
        dateFilter,
        ratingFilter,
        actionFilter,
        tagFilter,
        longFormSurveyAnswerFilters
      }
    ) {
      const customFilter = { ...getters.selectCustomFilter }
      const eventTypes = rootGetters['actionFilter/selectAnalyticEventTypesV2']

      if (filterComponent) {
        let collectionName
        switch (filterComponent) {
          case 'campaign-creation':
            collectionName = COLLECTION_NAMES.CUSTOMERS
            break
          default:
            collectionName = ''
        }
        customFilter.collectionName = collectionName
      }
      if (companyFilter) {
        customFilter.companyFilters = companyFilter.companies.length
          ? [
              {
                companies: companyFilter.companies,
                behavior: companyFilter.behavior || DEFAULT_BEHAVIOR
              }
            ]
          : []
      }
      if (locationFilter) {
        customFilter.locationFilters = locationFilter.locations.length
          ? [
              {
                locations: locationFilter.locations,
                behavior: locationFilter.behavior || DEFAULT_BEHAVIOR
              }
            ]
          : []
      }
      if (dateFilter) {
        customFilter.dateFilters = [
          {
            type: dateFilter.type,
            behavior: dateFilter.behavior || DEFAULT_BEHAVIOR,
            ...(dateFilter.type === 'ago'
              ? { unit: dateFilter.unit, unitQuantity: dateFilter.unitQuantity }
              : {}),
            ...(['range', 'since', 'on'].includes(dateFilter.type)
              ? { dates: dateFilter.dates }
              : {})
          }
        ]
      }
      if (ratingFilter) {
        customFilter.surveyFilters = ratingFilter.ratings.length
          ? [{ ratings: ratingFilter.ratings, behavior: ratingFilter.behavior || DEFAULT_BEHAVIOR }]
          : []
      }
      if (actionFilter) {
        customFilter.analyticEventFilters = actionFilter.actions.map((a) => ({
          types: [a.eventType],
          behavior: a.did ? INCLUDE : EXCLUDE,
          ...(a.eventType === eventTypes.SENT_CAMPAIGN && a.campaignId
            ? { campaigns: [a.campaignId] }
            : {}),
          ...(a.eventType === eventTypes.REDEEM_PROMO && a.promoId ? { promos: [a.promoId] } : {})
        }))
      }
      if (tagFilter) {
        customFilter.tagFilters = tagFilter.tags.length
          ? [
              {
                tags: tagFilter.tags.map((t) => t._id),
                tagNames: tagFilter.tags.map((t) => t.name),
                behavior: tagFilter.behavior || DEFAULT_BEHAVIOR
              }
            ]
          : []
      }
      if (longFormSurveyAnswerFilters) {
        customFilter.longFormSurveyAnswerFilters =
          longFormSurveyAnswerFilters.longFormSurveyQuestion
            ? [
                {
                  ...longFormSurveyAnswerFilters,
                  behavior: longFormSurveyAnswerFilters.behavior || DEFAULT_BEHAVIOR
                }
              ]
            : []
      }

      commit('SET_CUSTOM_FILTER', customFilter)
    }
  }
}
